import { useMemo, useEffect, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import Asterisk from '@decisiv/iconix/lib/components/Asterisk';
import { Flex, Message, Loading } from '@decisiv/ui-components/lib/components';
import Grid from '@decisiv/ui-components/lib/components/Grid';
import { TextField, Select, TextArea } from 'components/Form';
import { DropdownListItem } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { getStatesByCountry } from 'utils/countryRegions';

import { Trans, t } from '@lingui/macro';

import { COUNTRY_OPTIONS } from 'pages/Management/constants';
import { FormContainer } from './styles';
import { useManagedPlatformAccountsQuery } from './hooks';

const CLEAR_STATE_SELECT = t`Clear State`;

export default function DepotForm({ accountId }: { accountId?: string }) {
  const { watch, setValue } = useFormContext();
  const countryValue = watch('country');

  const {
    data: accounts,
    loading: loadingAccounts,
    loadingMore: loadingMoreAccounts,
    loadMore: loadMoreAccounts,
    hasNextPage: hasNextPageAccounts,
  } = useManagedPlatformAccountsQuery({
    variables: {
      page: 1,
    },
    skip: !!accountId,
  });

  const accountsOptions: DropdownListItem[] = useMemo(() => {
    if (!accounts?.managedPlatformAccounts?.collection) return [];

    const formattedData = accounts.managedPlatformAccounts.collection.map(
      (account) => ({
        id: account.id,
        label: account.name,
        value: account.id,
        sourceId: account.sourceId,
      }),
    );

    return formattedData;
  }, [accounts]);

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    if (!hasNextPageAccounts || loadingAccounts || loadingMoreAccounts) return;
    loadMoreAccounts();
  }, [
    hasNextPageAccounts,
    loadingAccounts,
    loadingMoreAccounts,
    loadMoreAccounts,
  ]);

  const stateOptions = useMemo(
    () => (countryValue ? getStatesByCountry(countryValue) : []),
    [countryValue],
  );

  const handleOnCountryChange = useCallback(() => {
    setValue('state', '');
  }, [setValue]);

  /* istanbul ignore next -- @preserve */
  const LoadingOptions = () => (
    <P size="small" color="charcoal" paddingTop={1} paddingBottom={0.2}>
      <Loading size="small" title={t`Loading Accounts...`} />
    </P>
  );

  return (
    <FormContainer>
      <Grid.Row>
        <Grid.Column>
          <Flex justifyContent="flex-end">
            <Message color="information" icon={Asterisk}>
              <Trans>Required Fields</Trans>
            </Message>
          </Flex>
        </Grid.Column>
      </Grid.Row>
      {!accountId && (
        <Grid.Row>
          <Select
            label={t`Account`}
            name="accountId"
            options={accountsOptions}
            renderOptionLabel={loadingAccounts ? LoadingOptions : undefined}
            defaultValue=""
            required
          />
        </Grid.Row>
      )}
      <Grid.Row>
        <TextField
          label={t`FMS ID`}
          name="dmsId"
          maxLength={10}
          helpMessage={t`Max 10 Characters (Alphanumeric only)`}
        />
      </Grid.Row>
      <Grid.Row>
        <TextArea
          label={t`Notes`}
          name="note"
          maxLength={350}
          helpMessage={t`You have ${
            350 - (watch('note')?.length || 0)
          } characters left (350 max).`}
        />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Name`} name="name" required />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Email`} name="email" required />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Address`} name="address" />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0} paddingRight={0.5}>
          <Select
            label={t`Country`}
            name="country"
            options={COUNTRY_OPTIONS}
            onChange={handleOnCountryChange}
            defaultValue=""
            required
          />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0} paddingLeft={0.5}>
          <Select
            clearButtonLabel={CLEAR_STATE_SELECT}
            label={t`State`}
            name="state"
            options={stateOptions}
            defaultValue=""
            required
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0} paddingRight={0.5}>
          <TextField label={t`City`} name="city" required />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0} paddingLeft={0.5}>
          <TextField label={t`Postal Code`} name="postalCode" />
        </Grid.Column>
      </Grid.Row>
    </FormContainer>
  );
}
